import { useState, useEffect } from "react";
import { fetchSingleCastle } from "../API/castles";
import styled from "styled-components";
import Text from "./Text";
import CastleVariant1 from "../assets/castle-variant-one.png";
import CastleVariant2 from "../assets/castle-variant-two.png";

const CastleDiv = styled.div`
  z-index: ${(props) => props.zindex};
  position: absolute;
  left: ${(props) => props.left + "%"};
  top: ${(props) => props.top + "%"};
  transform: translate(-50%, -50%);
  background-color: transparent;

  ${(props) =>
    (props.isFailed || (props.isConquered && !props.allowReplay)) &&
    `
    pointer-events: none;
    opacity: 0.6;
  `}

  &:hover .popup {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.5s ease 0.3s, visibility 0s linear 0.2s;
  }
`;

const Image = styled.img`
  width: ${({ variant, isFailed, isConquered }) => {
    if (isFailed || isConquered) return "52px"; // ⬅️ Make inactive/conquered castles bigger
    if (variant === 1) return "75px";
    if (variant === 2) return "65px";
    return "45px";
  }};

  height: ${({ variant, isFailed, isConquered }) => {
    if (isFailed || isConquered) return "52px"; // ⬅️ Same for height
    if (variant === 1) return "75px";
    if (variant === 2) return "65px";
    return "45px";
  }};

  object-fit: contain;

  &:hover {
    cursor: pointer;
    scale: 1.2;
    transition: scale 0.5s;
  }
`;






const Popup = styled.div`
  background-color: var(--bg-dark-blue);
  min-width: 180px;
  padding: 15px 5px;
  position: absolute;
  z-index: 1000;
  display: flex;
  visibility: hidden;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  text-align: center;
  border-radius: 6.96px;
  opacity: 0;
  left: 50%;
  transform: translateX(-50%); /* 👈 Center horizontally */

  ${(props) =>
    props.below
      ? `
        top: 100%; /* 👈 Start just below the icon */
        margin-top: 10px;

        &::after {
          content: "";
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 15px solid transparent;
          border-right: 15px solid transparent;
          border-bottom: 15px solid var(--bg-dark-blue);
        }
      `
      : `
        bottom: 100%; /* 👈 Start just above the icon */
        margin-bottom: 10px;

        &::after {
          content: "";
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 15px solid transparent;
          border-right: 15px solid transparent;
          border-top: 15px solid var(--bg-dark-blue);
        }
      `}
`;



const Circle = styled.div`
  border-radius: 50%;
  background-color: #fe6030;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;


const Shadow = styled.div`
  width: 37px;
  height: 20px;
  position: absolute;
  z-index: -1;
  border-radius: 50%;
  background: radial-gradient(
    ellipse at center,
    #181818 0%,
    rgba(104, 104, 104, 0) 100%
  );
  opacity: 0.5;
  filter: blur(1px);

  bottom: ${({ isFailed, isConquered }) =>
    isFailed || isConquered ? "8px" : "15px"};
  left: ${({ isFailed, isConquered }) =>
    isFailed || isConquered ? "10px" : "18px"};
`;




const Variants = {
  1: CastleVariant1,
  2: CastleVariant2
}


const Castle = ({
  left,
  top,
  open,
  handleClick,
  id,
  selectedColor,
  zindex,
  isFailed,
  isConquered,
  domId,
  variant
}) => {
  const [castleName, setCastleName] = useState("");

  const getCastleIconPath = (isFailed, isConquered, userColor) => {
    if (isFailed) {
      return `/assets/castles/Castle_icon_v2_inactive.png`; // Inactive for failed castles
    }
    if (isConquered) {
      return `/assets/castles/Castle_icon_v2_${userColor}.png`; // ✅ Use userColor if conquered
    }
    return `/assets/castles/Castle_icon_v2_default.png`; // Default color if not conquered
  };


  const getImageSrc = () => {
  if (variant === 2) {
    if (isFailed) {
      return `/assets/castles/Castle_icon_v1_inactive.png`;
    }
    if (isConquered) {
      return `/assets/castles/Castle_icon_v1_${selectedColor}.png`;
    }
    return CastleVariant2; // Default variant 2 SVG
  }

  // Variant 1 (default logic)
  if (isFailed) {
    return `/assets/castles/Castle_icon_v2_inactive.png`;
  }
  if (isConquered) {
    return `/assets/castles/Castle_icon_v2_${selectedColor}.png`;
  }
  return CastleVariant1;
};

  

  useEffect(() => {
    const fetchCastleData = async () => {
      try {
        const data = await fetchSingleCastle(id);
        if (data) {
          setCastleName(data.castle_name);
        } else {
          setCastleName("Unknown Castle");
        }
      } catch (error) {
        console.error("Error fetching castle name:", error);
        setCastleName("Unknown Castle");
      }
    };

    fetchCastleData();
  }, [id]);

  const imageSrc = getCastleIconPath(isFailed, isConquered, selectedColor);
  const allowReplay =
    sessionStorage.getItem("allow_replay_conquered") === "true";

  return (
    <CastleDiv
      id={domId}
      zindex={zindex}
      left={left}
      top={top}
      onClick={
        !isFailed && (!isConquered || allowReplay)
          ? () => handleClick(id)
          : undefined
      }
    >
      <Popup className="popup" below={id === 3}>

        <Text
          fontFamily={"Manrope"}
          color={"#F3DE8A"}
          fontSize={"16px"}
          fontWeight={700}
        >
          {castleName}
        </Text>
        <Circle>
  <div style={{ position: "relative", top: "-5%" }}>
  <Text
    fontFamily="'Source Serif 4', serif"
    color="#F3DE8A"
    fontSize="18px"
    fontWeight="bold"
  >
    {variant === 2 ? "x2" : "x3"}
  </Text>
</div>

</Circle>

      </Popup>

      <Image
  src={getImageSrc()}
  variant={variant} // 👈 Add this line
  isFailed={isFailed}
  isConquered={isConquered}
/>




<Shadow isFailed={isFailed} isConquered={isConquered} />

    </CastleDiv>
  );
};

export default Castle;
